import React, { useState, useEffect } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import Modal from '../Modal.js';
import Text from '../../Text.js';
import PasswordConfirmation from '../PasswordConfirmation.js';
import BeneficiariesSharesInputs from './BeneficiariesSharesInputs.js';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
import * as localizationMaps from '../../../localization/maps.js';
import validateSignage from '../validations/validateSignage.js';
import validateShares from './validations/validateShares.js';
import getCurrentBeneficiaries from './getCurrentBeneficiaries.js';
import { initialSignageState, initialSignageErrorState, beneficiariesSharesInit } from '../common/stateInitialization.js';

/**
 * Sets the initial beneficiary data.
 * @param {Object} beneficiaryData - Data of the beneficiary to be set.
 * @returns {Object} - Initial beneficiary data.
 */
const setBeneficiaryData = beneficiaryData => ({
  beneficiaryID: beneficiaryData ? beneficiaryData.beneficiaryID : null,
  firstname: beneficiaryData ? beneficiaryData.firstname : "",
  lastname: beneficiaryData ? beneficiaryData.lastname : "",
  relationship: beneficiaryData ? localizationMaps.fromEnglish("relationships", beneficiaryData.relationship) : localizationMaps.fromEnglish("relationships", "Child"),
  type: beneficiaryData ? localizationMaps.fromEnglish("beneficiariesOptions", beneficiaryData.type) : localizationMaps.fromEnglish("beneficiariesOptions", "Revocable"),
  shares: beneficiaryData ? beneficiaryData.shares : '',
  birthdate: beneficiaryData ? dates.formatIncomingDate(beneficiaryData.birthdate) : ''
});

/**
 * RemoveBeneficiaryModal component handles the modal for removing a beneficiary.
 * @param {Object} props - Properties passed to the component.
 * @returns {JSX.Element} - Rendered component.
 */
const RemoveBeneficiaryModal = ({
  open,
  loader,
  onClose,
  isContingent,
  removeBeneficiary,
  contingentBeneficiaries,
  primaryBeneficiaries,
  beneficiaryData,
  openChangeRequestForm,
  isAdmin
}) => {
  const [passwordConfirmationModalOpen, setPasswordConfirmationModalOpen] = useState(false);
  const [beneficiary, setBeneficiary] = useState(setBeneficiaryData(beneficiaryData));
  const [sharesError, setSharesError] = useState(false);
  const [signage, setSignage] = useState(initialSignageState);
  const [signageErrorState, setSignageErrorState] = useState(initialSignageErrorState);
  const [beneficiaryShares, setBeneficiaryShares] = useState({
    primaryBeneficiaries: [],
    contingentBeneficiaries: []
  });
  const {
    shares,
    beneficiaryID
  } = beneficiary;
  useEffect(() => {
    setBeneficiary(setBeneficiaryData(beneficiaryData));
    setPasswordConfirmationModalOpen(false);
    setSignage(initialSignageState);
    setBeneficiaryShares(beneficiariesSharesInit(primaryBeneficiaries, contingentBeneficiaries, beneficiaryID));
  }, [beneficiaryData]);

  /**
   * Handles the change in shares for a specific beneficiary.
   * @param {string} selectedBeneficiaryID - ID of the beneficiary whose shares are being updated.
   * @param {number} value - New shares value.
   */
  const handleSharesChange = (selectedBeneficiaryID, value) => {
    setBeneficiaryShares({
      primaryBeneficiaries: beneficiaryShares.primaryBeneficiaries.map(primaryBeneficiary => {
        if (primaryBeneficiary.beneficiaryID === selectedBeneficiaryID) {
          return {
            ...primaryBeneficiary,
            shares: value
          };
        }
        return primaryBeneficiary;
      }),
      contingentBeneficiaries: beneficiaryShares.contingentBeneficiaries.map(contingentBeneficiary => {
        if (contingentBeneficiary.beneficiaryID === selectedBeneficiaryID) {
          return {
            ...contingentBeneficiary,
            shares: value
          };
        }
        return contingentBeneficiary;
      })
    });
  };

  /**
   * Handles the change in signage fields.
   * @param {string} field - The field name to be updated.
   * @param {string} value - The new value for the field.
   */
  const handleSignageChange = (field, value) => {
    setSignage(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * Resets the form to its initial state.
   */
  const resetForm = () => {
    setPasswordConfirmationModalOpen(false);
    setSignage(initialSignageState);
    setSignageErrorState(initialSignageErrorState);
    setBeneficiary(setBeneficiaryData(beneficiaryData));
  };

  /**
   * 
   * @returns 
   */
  const createChangeRequest = async () => {
    const beneficiaries = await getCurrentBeneficiaries(beneficiaryShares, beneficiaryID);
    beneficiaries.contingent = isContingent ? beneficiaries.contingent : [];
    beneficiaries.primary = !isContingent ? beneficiaries.primary : [];
    if (isAdmin || beneficiary.type === "Irrevocable") {
      const changeRequestData = {
        type: `Remove ${localizationMaps.toEnglish('beneficiariesOptions', beneficiary.type)} Beneficiary`,
        details: {
          beneficiaryID,
          primary: beneficiaries.primary,
          contingent: beneficiaries.contingent
        }
      };
      openChangeRequestForm(changeRequestData);
      resetForm();
      return true;
    }
    return false;
  };

  /**
   * Validates the shares of the beneficiary.
   * @returns {boolean} - Returns true if validation is successful, otherwise false.
   */
  const validateBeneficiaryShares = async () => {
    const isDeleteBeneficiary = true;
    const sharesErrorValidation = await validateShares(beneficiaryShares, isContingent, shares, beneficiaryID, isDeleteBeneficiary);
    if (sharesErrorValidation) {
      setSharesError(sharesErrorValidation);
      return false;
    }
    return true;
  };

  /**
   * 
   */
  const validateSharesAndProceedToNextStep = async () => {
    const sharesValid = await validateBeneficiaryShares();
    if (!sharesValid) {
      return false;
    }
    setSharesError(false);
    const changeRequestRequired = await createChangeRequest();
    if (!changeRequestRequired) {
      return setPasswordConfirmationModalOpen(true);
    }
    return false;
  };

  /**
   * Closes the modal and resets the form.
   */
  const close = () => {
    resetForm();
    onClose();
  };

  /**
   * Saves the beneficiary removal after validation.
   * @returns {boolean|null} - Returns null after execution.
   */
  const save = async () => {
    const formErrors = await validateSignage({
      ...signage
    });
    if (formErrors !== false) {
      setSignageErrorState({
        ...formErrors
      });
      return false;
    }
    const beneficiaries = await getCurrentBeneficiaries(beneficiaryShares, beneficiaryID);
    beneficiaries.contingent = isContingent ? beneficiaries.contingent : [];
    beneficiaries.primary = !isContingent ? beneficiaries.primary : [];
    const success = await removeBeneficiary(beneficiaryID, signage.password, beneficiaries, {
      name: signage.name,
      signature: signage.signature,
      acceptedTerms: signage.acceptedTerms,
      timestampOfAcceptance: dates.getUTC(),
      datedOn: dates.formatOutgoingDate(signage.datedOn)
    }, isContingent);
    if (success) {
      close();
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(Modal, {
    open: open,
    loader: loader,
    onClose: close,
    onBack: passwordConfirmationModalOpen ? () => setPasswordConfirmationModalOpen(false) : null,
    title: i18n.t(tokens.titles["remove beneficiary"]),
    primaryButton: {
      label: passwordConfirmationModalOpen ? i18n.t(tokens.buttons.finish) : i18n.t(tokens.buttons.save),
      onClick: passwordConfirmationModalOpen ? save : validateSharesAndProceedToNextStep
    }
  }, passwordConfirmationModalOpen ? /*#__PURE__*/React.createElement(PasswordConfirmation, {
    handleInputChange: handleSignageChange,
    signage: signage,
    signageErrorState: signageErrorState,
    title: i18n.t(tokens.labels.account.confirmBeneficiaryChanges)
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, isContingent ? /*#__PURE__*/React.createElement(Text, {
    light: true,
    small: true,
    margin: "20px 0 5px 0"
  }, i18n.t(tokens.labels.beneficiaries.pleaseEnsureAllContingentBeneficiaries)) : /*#__PURE__*/React.createElement(Text, {
    light: true,
    small: true,
    margin: "20px 0 5px 0"
  }, i18n.t(tokens.labels.beneficiaries.pleaseEnsureAllPrimaryBeneficiaries)), /*#__PURE__*/React.createElement(BeneficiariesSharesInputs, {
    isContingent: isContingent,
    contingentBeneficiaries: contingentBeneficiaries,
    primaryBeneficiaries: primaryBeneficiaries,
    beneficiaryID: beneficiaryID,
    sharesError: sharesError,
    handleSharesChange: handleSharesChange
  })));
};
export default RemoveBeneficiaryModal;